import {FC, SyntheticEvent, useState} from 'react'
import {Redirect, useHistory, useLocation, useParams} from 'react-router-dom'
import {useDispatch} from 'react-redux'
import queryString from 'query-string'
import {FormProvider, SubmitHandler, useFieldArray, useForm} from 'react-hook-form'
import {yupResolver} from '@hookform/resolvers/yup'
import {groupBy, zip} from 'lodash'
import Button from 'jbc-front/components/Button'
import * as FileDrop from 'jbc-front/components/presenters/form/FileDrop'
import {ErrorMessage} from 'jbc-front/components/presenters/form/ErrorMessage'
import {Section} from 'jbc-front/components/presenters/form/Section'
import {LoadingPage} from 'components/ui/LoadingPage'
import {Body, Head, Panel} from 'components/ui/Panel'
import {FileField} from 'components/reactHookForm/FileField'
import {Label} from 'components/forms/Label'
import {PreviewProcedurePdfModal, PreviewProcedurePdfData} from 'components/modals/PreviewProcedurePdfModal'
import {useMutation, useQuery} from 'hooks/graphql'
import {useSelector} from 'hooks/redux'
import {useNotify} from 'hooks/useNotify'
import {fullNameFormat} from 'libs/formatter'
import {asyncError} from 'store/actions/asyncError'
import {FilePickerModal} from './parts/FilePickerModal'
import {EgovFormValidationError, ValidationError} from './parts/ValidationError/ValidationError'
import {
  EGOV_INFORMATIONS,
  EgovInformationsQueryResult,
  EgovInformationsQueryVariables,
  PREPARE_EGOV_PROCEDURE,
  PrepareEgovProcedureQueryResult,
  PrepareEgovProcedureQueryVariables,
  ProcedureStatus,
} from './query'
import {Schema, schema} from './schema'
import styles from './EgovPrepare.scss'

// @ts-ignore
import ListGroup from 'jbc-front/components/ListGroup'

interface RouteParams {
  id: string
}

interface SearchParams {
  procedure_code?: string
}

export const EgovPrepare: FC = () => {
  const notify = useNotify()
  const planType = useSelector((state) => state.client.current.plan_type)

  const {id: procedureStatusId} = useParams<RouteParams>()
  const location = useLocation()
  const {procedure_code: egovProcedureCode}: SearchParams = queryString.parse(location.search, {arrayFormat: 'bracket'})

  const {data, loading} = useQuery<EgovInformationsQueryResult, EgovInformationsQueryVariables>(EGOV_INFORMATIONS, {
    variables: {
      procedureStatusId,
    },
    fetchPolicy: 'network-only',
  })
  const procedureStatus = data?.client.procedureStatus

  if (planType === 'free_plan') {
    notify('無料プランのため利用できません。', 'warning')
    return <Redirect to={`/procedure_statuses/${procedureStatusId}`} />
  }

  if (!egovProcedureCode) {
    return <Redirect to={`/procedure_statuses/${procedureStatusId}`} />
  }

  if (!loading && !procedureStatus) {
    return <Redirect to="/procedure_statuses" />
  }

  return (
    <div>
      <div className="l-title-wrap">
        <div className="m-title-main">電子申請の準備</div>
      </div>
      <div className="l-wrap-xs">
        {procedureStatus ? (
          <Form procedureStatus={procedureStatus} egovProcedureCode={egovProcedureCode} />
        ) : (
          <LoadingPage />
        )}
      </div>
    </div>
  )
}

interface FormProps {
  procedureStatus: ProcedureStatus
  egovProcedureCode: string
}

const Form: FC<FormProps> = ({procedureStatus, egovProcedureCode}) => {
  const dispatch = useDispatch()
  const notify = useNotify()
  const history = useHistory()

  const methods = useForm<Schema>({
    resolver: yupResolver(schema),
    defaultValues: {
      attachments: [],
      others: [],
    },
    mode: 'onBlur',
  })
  const {
    control,
    watch,
    handleSubmit,
    setValue,
    formState: {isSubmitting, errors},
  } = methods
  const attachments = watch('attachments')
  const {fields, append, remove} = useFieldArray({control, name: 'others', keyName: '_key'})

  const [isOpenFilePickerModal, setIsOpenFilePickerModal] = useState(false)
  const [egovFormValidationErrors, setEgovFormValidationErrors] = useState<EgovFormValidationError[]>([])
  const [previewPdfData, setPreviewPdfData] = useState<PreviewProcedurePdfData>()

  const [prepareEgovProcedure] = useMutation<PrepareEgovProcedureQueryResult, PrepareEgovProcedureQueryVariables>(
    PREPARE_EGOV_PROCEDURE
  )

  const info = procedureStatus?.egovInformations.find((info) => info.procedureCode === egovProcedureCode)
  if (!info) {
    return <Redirect to={`/procedure_statuses/${procedureStatus.id}`} />
  }

  const onSubmit: SubmitHandler<Schema> = async (data) => {
    setEgovFormValidationErrors([])

    const recommendedAttachments = groupBy(zip(info.recommendedAttachments, data.attachments), ([ra]) => ra?.formId)
    const applications = info.formId.map((formId, index) => {
      const attachments = []
      if (index === 0 && data.others.length) {
        // その他の添付書類は先頭の様式IDに紐付ける
        for (const file of data.others) {
          switch (file.type) {
            case 'ProcedureAttachment':
              attachments.push({
                documentName: 'その他',
                procedureAttachmentId: file.id,
              })
              break
            case 'UploadFile':
              attachments.push({
                documentName: 'その他',
                file: file.file,
              })
              break
            default:
              return file satisfies never // exhaustiveness check
          }
        }
      }
      for (const [ra, file] of recommendedAttachments[formId] || []) {
        if (ra && file) {
          attachments.push({
            documentName: ra.key,
            file,
          })
        }
      }

      return {formId, attachments}
    })

    try {
      const {data} = await prepareEgovProcedure({
        variables: {
          input: {
            procedureStatusId: procedureStatus.id,
            procedureCode: egovProcedureCode,
            applications,
          },
        },
        fetchPolicy: 'no-cache',
      })

      const errors = data?.prepareEgovProcedure.errors
      if (errors && typeof errors === 'object') {
        let validationErrors: EgovFormValidationError[] = []
        if ('validationErrors' in errors) {
          validationErrors = errors.validationErrors as EgovFormValidationError[]
        } else if ('_base' in errors && Array.isArray(errors._base)) {
          validationErrors = errors._base.map((e: string) => ({errorMessage: e}))
        }
        setEgovFormValidationErrors(validationErrors)
      } else {
        notify('申請準備が完了しました', 'success')
        history.push(`/procedure_statuses/${procedureStatus.id}`)
      }
    } catch (err) {
      dispatch(asyncError(err))
    }
  }

  return (
    <FormProvider {...methods}>
      {egovFormValidationErrors.length > 0 && (
        <Section title="入力内容に誤りがあります。修正後に再度申請をお願いします。" error>
          {egovFormValidationErrors.map((error, index) => (
            <ValidationError key={index} error={error} />
          ))}
        </Section>
      )}

      <Panel>
        <Head className={styles.formHead}>新規申請内容</Head>
        <Body className={styles.formBody}>
          <ListGroup>
            <ListGroup.Title>帳票一覧</ListGroup.Title>
            {info.reports.map((report) => (
              <ListGroup.Item
                key={report.kind}
                as="a"
                onClick={(event: SyntheticEvent) => {
                  event.preventDefault()
                  const url = `/api/procedure_statuses/${procedureStatus.id}/pdf?type=${report.kind}`
                  setPreviewPdfData({
                    title: report.name,
                    num: report.kind,
                    edit: true,
                    url,
                    pdf: url,
                    procedureStatusId: procedureStatus.id,
                  })
                }}
              >
                {report.name}
              </ListGroup.Item>
            ))}
          </ListGroup>

          <ListGroup>
            <ListGroup.Title>対象従業員</ListGroup.Title>
            <ListGroup.Item>
              {fullNameFormat(procedureStatus.employee.lastName, procedureStatus.employee.firstName)}
            </ListGroup.Item>
          </ListGroup>

          <Panel className={styles.attachmentsPanel}>
            <Head className={styles.formHead}>添付書類</Head>
            <Body className={styles.attachmentsBody}>
              {info.recommendedAttachments.map((attachment, index) => (
                <FileField
                  name={`attachments.${index}`}
                  label={attachment.displayName}
                  key={index}
                  required={attachment.required}
                  hint={attachment.note}
                  accept={{'image/*': ['.jpg'], 'application/pdf': ['.pdf']}}
                >
                  {attachments[index] && (
                    <FileField.FileContainer>
                      <FileField.File
                        file={attachments[index]}
                        onRemove={() => {
                          setValue(`attachments.${index}`, null as unknown as File)
                        }}
                      />
                    </FileField.FileContainer>
                  )}
                </FileField>
              ))}
              <div>
                {info.recommendedAttachments.length > 0 && <Label>その他</Label>}
                <Button className={styles.addAttachmentButton} widthAuto onClick={() => setIsOpenFilePickerModal(true)}>
                  ファイルを選択
                </Button>
                {fields.length > 0 && (
                  <>
                    <FileDrop.FilesHeader>アップロードファイル</FileDrop.FilesHeader>
                    <FileDrop.Files>
                      {fields.map((field, index) => (
                        <FileDrop.File
                          key={field._key}
                          file={field.file}
                          downloadable={false}
                          onRemove={() => remove(index)}
                        />
                      ))}
                    </FileDrop.Files>
                  </>
                )}
                {errors.others?.message && <ErrorMessage>{errors.others.message}</ErrorMessage>}
              </div>
            </Body>
          </Panel>
        </Body>
      </Panel>

      {procedureStatus.procedureType === 'enroll' && (info.procedureKind === 'r1' || info.procedureKind === 'r2') && (
        <div className={styles.warning}>
          注意：電子申請では従業員情報に登録されている被保険者整理番号は申請されません
        </div>
      )}

      <div className="u-ta-c u-mt50">
        <Button primary onClick={handleSubmit(onSubmit)} disabled={isSubmitting}>
          申請準備する
        </Button>
      </div>

      <FilePickerModal
        procedureStatus={procedureStatus}
        isOpen={isOpenFilePickerModal}
        onClose={() => setIsOpenFilePickerModal(false)}
        onSubmit={(files) => {
          for (const file of files) {
            switch (file.type) {
              case 'ProcedureAttachment':
                if (!fields.find((f) => f.type === 'ProcedureAttachment' && f.id === file.id)) {
                  append(file)
                }
                break
              case 'UploadFile':
                if (!fields.find((f) => f.type === 'UploadFile' && f.file === file.file)) {
                  append(file)
                }
                break
              default:
                return file satisfies never // exhaustiveness check
            }
          }
          setIsOpenFilePickerModal(false)
        }}
      />

      <PreviewProcedurePdfModal
        data={previewPdfData}
        procedureStatus={{
          procedureType: procedureStatus.procedureType,
          employeeId: procedureStatus.employee.id,
          healthInsuranceType: procedureStatus.employee.currentOffice?.healthInsuranceType,
          isRelatedRecordsDirty: procedureStatus.isRelatedRecordsDirty,
        }}
        onClose={() => setPreviewPdfData(undefined)}
      />
    </FormProvider>
  )
}
